import Phaser from 'phaser';
import { RunManager } from '../systems/RunManager';
import { SfxManager } from '../utils/SfxManager';
import { ITEM_DEFINITIONS, SLOT_LABELS } from '../data/items';
import { PERSONAL_SLOTS } from '../types/Item.types';
import { MusicManager } from '../systems/MusicManager';
import { GAME_W, GAME_H, GAME_CX, GAME_CY } from '../utils/MobileHelper';
import { startSceneWithFade } from '../utils/SceneUtils';
import { FONT_BODY } from '../utils/Typography';

// === SCÈNE D'EXTRACTION ===
// Le joueur choisit de s'extraire (garder le loot, retour au camp)
// ou de continuer le run (plus de risque, plus de loot).
// Utilise InventoryManager comme source de vérité unique.

export class ExtractScene extends Phaser.Scene {
  private run!: RunManager;
  private sfx = new SfxManager();

  constructor() {
    super({ key: 'ExtractScene' });
  }

  init(data: { run: RunManager }): void {
    this.run = data.run;
  }

  create(): void {
    this.cameras.main.fadeIn(200, 0, 0, 0);
    this.add.rectangle(GAME_CX, GAME_CY, GAME_W, GAME_H, 0x0a0a1e);

    // Titre
    this.add.text(GAME_CX, 60, '⛵ POINT D\'EXTRACTION', {
      fontSize: '22px', color: '#2ecc71', fontFamily: FONT_BODY, fontStyle: 'bold',
    }).setOrigin(0.5);

    this.add.text(GAME_CX, 95, 'Un canot vous attend. Partez maintenant\net gardez tout votre butin... ou continuez.', {
      fontSize: '12px', color: '#7f8c8d', fontFamily: FONT_BODY, align: 'center',
    }).setOrigin(0.5);

    // Étage actuel et avertissement
    const floorLabel = this.run.getCurrentFloorLabel();
    this.add.text(GAME_CX, 135, `📍 ${floorLabel}`, {
      fontSize: '11px', color: '#f1c40f', fontFamily: FONT_BODY,
    }).setOrigin(0.5);

    // === RÉSUMÉ DU BUTIN ===
    this.add.text(GAME_CX, 175, '--- Butin actuel ---', {
      fontSize: '12px', color: '#f39c12', fontFamily: FONT_BODY,
    }).setOrigin(0.5);

    let y = 200;
    const inv = this.run.getInventory();

    // Colonne gauche : 200 desktop (GAME_CX-200=200), 25 mobile (GAME_CX-200=25)
    const lx = GAME_CX - 200;
    const lx2 = lx + 15;

    // Équipement porté par chaque membre
    for (const member of this.run.getTeam()) {
      if (!member.alive) continue;
      const equip = inv.getEquipment(member.id);
      const hasEquip = PERSONAL_SLOTS.some(s => equip[s] !== null);
      if (!hasEquip) continue;
      this.add.text(lx, y, `${member.name}:`, {
        fontSize: '10px', color: '#f1c40f', fontFamily: FONT_BODY,
      });
      y += 16;
      for (const slot of PERSONAL_SLOTS) {
        const item = equip[slot];
        if (item) {
          const def = ITEM_DEFINITIONS[item.definitionId];
          if (def) {
            this.add.text(lx2, y, `${SLOT_LABELS[slot]}  ${def.name}`, {
              fontSize: '10px', color: '#bdc3c7', fontFamily: FONT_BODY,
            });
            y += 18;
          }
        }
      }
    }

    // Sac à dos partagé
    const bag = inv.getBackpack();
    if (bag) {
      const bagDef = ITEM_DEFINITIONS[bag.definitionId];
      if (bagDef) {
        this.add.text(lx, y, `🎒 ${bagDef.name}`, {
          fontSize: '10px', color: '#bdc3c7', fontFamily: FONT_BODY,
        });
        y += 18;
      }
    }

    // Items dans le transport
    const transport = inv.getTransport();
    if (transport.length > 0) {
      this.add.text(lx, y, `🎒 Transport : ${transport.length} item(s)`, {
        fontSize: '10px', color: '#7f8c8d', fontFamily: FONT_BODY,
      });
      y += 18;
    }

    // Or
    const gold = this.run.getGold();
    this.add.text(lx, y, `💰 Or : ${gold}`, {
      fontSize: '10px', color: '#f1c40f', fontFamily: FONT_BODY,
    });
    y += 30;

    // === CHOIX ===

    // Extraire : retour au camp avec le loot
    const extractBtn = this.add.text(GAME_CX, y + 20, '[ ⛵ S\'extraire — Garder le butin ]', {
      fontSize: '16px', color: '#2ecc71', fontFamily: FONT_BODY,
      backgroundColor: '#1a2a1a', padding: { x: 20, y: 10 },
    }).setOrigin(0.5);
    extractBtn.setInteractive({ useHandCursor: true });
    extractBtn.on('pointerdown', () => {
      this.sfx.extract();
      this.doExtract();
    });
    extractBtn.on('pointerover', () => extractBtn.setColor('#27ae60'));
    extractBtn.on('pointerout', () => extractBtn.setColor('#2ecc71'));

    this.add.text(GAME_CX, y + 55, 'Retour au camp. Tout le loot est sauvé.', {
      fontSize: '10px', color: '#7f8c8d', fontFamily: FONT_BODY,
    }).setOrigin(0.5);

    // Continuer : plus de risque
    const continueBtn = this.add.text(GAME_CX, y + 100, '[ ⚔️ Continuer l\'expédition ]', {
      fontSize: '16px', color: '#e74c3c', fontFamily: FONT_BODY,
      backgroundColor: '#2c1a1a', padding: { x: 20, y: 10 },
    }).setOrigin(0.5);
    continueBtn.setInteractive({ useHandCursor: true });
    continueBtn.on('pointerdown', () => {
      this.sfx.select();
      this.run.completeCurrentNode();
      startSceneWithFade(this, 'MapScene', { run: this.run });
    });
    continueBtn.on('pointerover', () => continueBtn.setColor('#c0392b'));
    continueBtn.on('pointerout', () => continueBtn.setColor('#e74c3c'));

    this.add.text(GAME_CX, y + 135, 'Plus de risque, mais aussi plus de butin.', {
      fontSize: '10px', color: '#7f8c8d', fontFamily: FONT_BODY,
    }).setOrigin(0.5);

    // Avertissement permadeath
    this.add.text(GAME_CX, y + 175, '⚠️ Si vous mourez, tout est perdu. Permadeath.', {
      fontSize: '10px', color: '#e74c3c', fontFamily: FONT_BODY,
    }).setOrigin(0.5);
  }

  /** Extraction : le joueur quitte le run en victoire avec bonus or par étage */
  private doExtract(): void {
    this.run.completeCurrentNode();

    // Bonus or d'extraction : +5 or par étage atteint
    const currentNode = this.run.getMap().nodes.find(n => n.id === this.run.getMap().currentNodeId);
    const floor = currentNode?.floor ?? 0;
    const extractBonus = floor * 5;
    if (extractBonus > 0) this.run.addGold(extractBonus);

    this.run.getMeta().endRun(true);
    RunManager.clearSavedRun();
    MusicManager.playJingle(this, 'victory');
    startSceneWithFade(this, 'RunEndScene', { run: this.run, victory: true });
  }
}
